import { useEffect, useMemo, useState, type MutableRefObject } from 'react';
import { cn, MicroLabel } from '../ui/primitives';
import type { BrandLayoutSection } from '../types';

/**
 * The brand's sections, in one column with an index rail beside them.
 *
 * ── Why a rail and not tabs ──
 *
 * `@codezo/quote-detail` went the other way, and deliberately: a quote is a set
 * of separate jobs (the offer, the client, the settings) and only one is being
 * worked at a time, so hiding the rest is a feature.
 *
 * A brand page is the opposite — it IS one document, read top to bottom, and
 * the thing people do with it is send someone to a part of it ("the logo rules
 * are halfway down"). Tabs break that: a deep link lands on a panel that isn't
 * open, and "scroll to the section" has to open a tab first. So the sections
 * stay in one scroll and the rail tracks where you are in it.
 *
 * ── Where the active state comes from ──
 *
 * From the scroll position, via IntersectionObserver, NOT from clicks. A rail
 * that only updates on click lies the moment someone scrolls, and both are
 * ways of expressing the same thing: which section is in front of you.
 */

export function sectionAnchorId(id: string) {
	return `brand-sec-${id}`;
}

export function scrollToSection(id: string) {
	document.getElementById(sectionAnchorId(id))?.scrollIntoView({
		behavior: 'smooth',
		block: 'start',
	});
}

export function BrandSections({
	sections,
	revealRef,
	className,
}: {
	sections: BrandLayoutSection[];
	/**
	 * Filled with "take me to this section", for anything outside the sections
	 * that points at one — an overview stat that jumps to what it counts.
	 *
	 * A ref rather than context because the things that need it are props
	 * evaluated in the caller, outside any provider this could wrap around its
	 * own children. Same reasoning as the quote package's.
	 */
	revealRef?: MutableRefObject<((sectionId: string) => void) | null>;
	className?: string;
}) {
	const shown = useMemo(() => sections.filter((s) => !s.hidden), [sections]);
	const [active, setActive] = useState(() => shown[0]?.id ?? '');

	/* The list moves with the data — a brand that loses its last font loses the
	   typography section. If the active id goes with it, fall to the first
	   rather than highlighting nothing. */
	const idKey = shown.map((s) => s.id).join('|');
	useEffect(() => {
		const ids = idKey ? idKey.split('|') : [];
		if (ids.length > 0 && !ids.includes(active)) setActive(ids[0]);
	}, [idKey, active]);

	useEffect(() => {
		const ids = idKey ? idKey.split('|') : [];
		if (ids.length === 0) return;

		/* The band is deliberately narrow and high on the page: it marks the
		   section whose top has just passed under the header, which is the one
		   a reader considers themselves to be in. A full-height root would
		   report every section the tall ones overlap. */
		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (entry.isIntersecting) {
						setActive(entry.target.id.replace('brand-sec-', ''));
					}
				}
			},
			{ rootMargin: '-15% 0px -75% 0px' }
		);

		for (const id of ids) {
			const el = document.getElementById(sectionAnchorId(id));
			if (el) observer.observe(el);
		}
		return () => observer.disconnect();
	}, [idKey]);

	useEffect(() => {
		if (!revealRef) return;
		revealRef.current = scrollToSection;
		return () => {
			revealRef.current = null;
		};
	}, [revealRef]);

	return (
		<div className={cn('grid gap-8 lg:grid-cols-[12rem_minmax(0,1fr)]', className)}>
			<nav className="hidden lg:block">
				<div className="sticky top-5">
					<MicroLabel className="block px-2.5 pb-2">Guidelines</MicroLabel>
					{shown.map((section) => {
						const on = active === section.id;
						return (
							<button
								key={section.id}
								type="button"
								onClick={() => scrollToSection(section.id)}
								className={cn(
									'relative flex w-full items-center gap-2.5 rounded-[var(--r-2)] px-2.5 py-1.5 text-left text-xs transition-colors duration-[var(--dur-fast)]',
									on
										? 'bg-surface-fill text-text-1'
										: 'bg-transparent text-text-3 hover:text-text-1'
								)}
							>
								{on && (
									<span className="absolute -left-2 top-1/4 bottom-1/4 w-0.5 rounded-full bg-accent" />
								)}
								<span className="truncate">{section.label}</span>
								{section.count !== undefined && (
									<span className="ml-auto font-mono text-2xs text-text-4">
										{section.count}
									</span>
								)}
							</button>
						);
					})}
				</div>
			</nav>

			<div className="flex min-w-0 flex-col gap-11">
				{shown.map((section) => (
					<section
						key={section.id}
						id={sectionAnchorId(section.id)}
						/* The rail's own sticky offset, so a click lands the
						   heading below the header rather than under it. */
						className="scroll-mt-5"
					>
						{section.content}
					</section>
				))}
			</div>
		</div>
	);
}
